import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    TopToolbar,
    CreateButton,
    ExportButton,
    useRecordContext,
    useGetOne,
} from 'react-admin';
import ThumbnailField from '../../common/ThumbnailField';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingSection } from '../../common/LoadingSection';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import { CopyAll, Edit, FileUpload } from '@mui/icons-material';
import { Sections } from '../../custom';
import QuestionsLinkField from './QuestionsLinkField';
import { NoDataSection } from '../../common/NoDataSection';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import { DeleteWithDialogAction } from '../../common/DeleteWithDialogAction';
import { CustomDelete } from '../../common/CustomDelete';
import { BulkUploadSectionQuestions } from './SectionQuestionUpload';
import { ChevronRightIcon } from 'lucide-react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { BulkUploadQuestionBankAction } from '../questions_bank/BulkUploadQuestions';
import { BulkUploadQuestionBankViaDocAction } from '../questions_bank/BulkUploadUsingDocs';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import CommonModal from '../../common/ui/CommonModal';
import BulkUploadCreate from '../bulk-uploads/BulkUploadCreate';

const ListActions = ({
    programId,
    testSeriesId,
    testId,
}: {
    programId: string;
    testId: string;
    testSeriesId: string;
}) => {
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <CreateButton
                onClick={e => {
                    e.preventDefault();
                    navigate(`/test-series/${testSeriesId}/tests/${testId}/sections/create`);
                }}
            />
            <ExportButton />
        </TopToolbar>
    );
};

const EditButton = () => {
    const navigate = useNavigate();
    const record: Sections = useRecordContext<Sections>();
    const { testSeriesId, programId, testId } = useParams();

    return (
        <Button
            startIcon={<Edit />}
            onClick={e => {
                navigate(`/test-series/${testSeriesId}/tests/${testId}/sections/${record.id}/edit`);
            }}
        >
            Edit
        </Button>
    );
};

export const CopyIDButton = () => {
    const record = useRecordContext();
    return (
        <Button
            startIcon={<CopyAll />}
            onClick={e => {
                navigator.clipboard.writeText(record.id as string);
            }}
        >
            Copy ID
        </Button>
    );
};

const Actions = () => {
    const [showActions, setShowActions] = React.useState(false);
    const [bulkUploadModal, setBulkUploadModal] = React.useState(false);
    React.useEffect(() => {
        const handleClick = () => {
            setShowActions(false);
        };

        if (showActions) {
            setTimeout(() => {
                document.addEventListener('click', handleClick);
            }, 0);
        }

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [showActions]);

    const handleButtonClick = (e: any) => {
        e.stopPropagation();
        setShowActions((prev: any) => !prev);
    };

    const preventClose = (e: any) => {
        e.stopPropagation();
    };

    return (
        <div className="relative inline-block">
            <button onClick={handleButtonClick} className="p-2 rounded-full hover:bg-gray-200">
                <MoreVertIcon />
            </button>

            {showActions && (
                <div
                    onClick={preventClose}
                    className="absolute right-0 mt-2 w-[250px] bg-white shadow-lg rounded-lg p-2 flex flex-col text-start gap-2 z-10"
                >
                    <div className="flex w-full gap-2 justify-start text-[#4f3cc9] text-md items-center cursor-pointer hover:bg-blue-50">
                        <BulkUploadSectionQuestions setShowActions={setShowActions} />
                    </div>
                    <div className="flex w-full justify-start gap-2 text-[#4f3cc9] text-md items-center">
                        <BulkUploadQuestionBankAction
                            path={'/sections'}
                            setShowActions={setShowActions}
                        />
                    </div>
                    <div className="flex w-full gap-2 justify-start text-[#4f3cc9] text-md items-center hover:bg-blue-50">
                        <BulkUploadQuestionBankViaDocAction
                            path={'/test'}
                            setShowActions={setShowActions}
                        />
                    </div>
                    <div className="flex w-full font-semibold p-2  gap-2 justify-start text-[#4f3cc9] text-md items-center cursor-pointer hover:bg-blue-50">
                        <button
                            onClick={() => setBulkUploadModal(true)}
                            className="flex items-center uppercase gap-2"
                        >
                            <FileUpload fontSize="medium" /> Bulk Upload
                        </button>
                    </div>
                </div>
            )}
            <CommonModal
                title="Questions Bulk Upload"
                open={bulkUploadModal}
                setOpen={setBulkUploadModal}
            >
                <BulkUploadCreate selectedType="TEST_QUESTION_UPLOAD" />
            </CommonModal>
        </div>
    );
};

Actions.defaultProps = {
    label: 'Actions',
};

const SectionList = () => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const { testSeriesId, programId, testId } = useParams();
    if (!testId) {
        return <LoadingSection />;
    }
    return (
        <List
            title={<SectionListTitle />}
            empty={
                <NoDataSection
                    resource={'sections'}
                    onCreate={() => {
                        navigate(`/test-series/${testSeriesId}/tests/${testId}/sections/create`);
                    }}
                />
            }
            resource={'sections'}
            actions={
                <ListActions
                    programId={programId || ''}
                    testSeriesId={testSeriesId || ''}
                    testId={testId || ''}
                />
            }
            filter={{
                testId: testId,
            }}
        >
            <Datagrid bulkActionButtons={false}>
                <CopyIDButton />
                <TextField source="name" />
                <FormattedDateTimeField source="createdAt" />
                <QuestionsLinkField />
                <EditButton />
                <ConfirmDeleteButton
                    path={`test-series/${testSeriesId}/tests/${testId}/sections`}
                />
                <Actions />
            </Datagrid>
        </List>
    );
};

const SectionListTitle = () => {
    const { testSeriesId, testId } = useParams();
    const { data: testSeries, isLoading: loadingTestSeries } = useGetOne('test-series', {
        id: testSeriesId,
    });
    const { data: test, isLoading: loadingTest } = useGetOne('tests', { id: testId });

    if (loadingTestSeries || loadingTest) return <span>...</span>;

    return (
        <span className="flex flex-row items-center text-lg font-semibold">
            <Link to={`/test-series/`} className="text-blue-600 hover:underline">
                {testSeries?.title}
            </Link>
            <ChevronRightIcon />
            <Link
                to={`/test-series/${testSeriesId}/tests`}
                className="text-blue-600 hover:underline"
            >
                {test?.name}
            </Link>
            <ChevronRightIcon />
            <span>Sections</span>
        </span>
    );
};
export default SectionList;
